import { useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { Route } from "lucide-react";
import { useExpenses } from "../../lib/expenses/useExpenses";

const IRS_RATE = 0.67; // 2024 standard mileage rate

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

export function Mileage() {
  const navigate = useNavigate();
  const { expenses, add } = useExpenses();

  // Form state
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [date, setDate] = useState(today());
  const [miles, setMiles] = useState<string>("");
  const [roundTrip, setRoundTrip] = useState(false);
  const [purpose, setPurpose] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live calculation
  const milesNum = parseFloat(miles) || 0;
  const totalMiles = roundTrip ? milesNum * 2 : milesNum;
  const amountCents = Math.round(totalMiles * IRS_RATE * 100);
  const amountDisplay = formatCents(amountCents);

  // Mileage history: filter expenses with categoryLabel === "Mileage"
  const mileageTrips = useMemo(
    () => expenses.filter((e) => e.categoryLabel === "Mileage"),
    [expenses],
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!from.trim() || !to.trim() || !date || milesNum <= 0) {
      setError("Please fill in all required fields with valid values.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const vendor = roundTrip
        ? `${from.trim()} ↔ ${to.trim()}`
        : `${from.trim()} → ${to.trim()}`;
      await add({
        vendor,
        occurredOn: date,
        amountCents,
        currency: "USD",
        businessPurpose: purpose.trim() || undefined,
        categoryLabel: "Mileage",
      });
      // Reset form
      setFrom("");
      setTo("");
      setDate(today());
      setMiles("");
      setRoundTrip(false);
      setPurpose("");
      navigate("/inbox");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Mileage</h1>
        <p className="text-slate-500 mt-1">
          Log business trips and calculate deductions at the IRS standard rate.
        </p>
      </div>

      {/* Log a trip form */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <Route className="w-5 h-5 text-[#0070F2]" />
          <h2 className="text-base font-semibold text-slate-800">Log a trip</h2>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-slate-700" htmlFor="mileage-from">
                From <span className="text-red-500">*</span>
              </label>
              <input
                id="mileage-from"
                type="text"
                placeholder="123 Main St or City, State"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                required
                className="px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-slate-700" htmlFor="mileage-to">
                To <span className="text-red-500">*</span>
              </label>
              <input
                id="mileage-to"
                type="text"
                placeholder="456 Office Blvd or City, State"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                required
                className="px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-slate-700" htmlFor="mileage-date">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                id="mileage-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-slate-700" htmlFor="mileage-miles">
                Miles <span className="text-red-500">*</span>
              </label>
              <input
                id="mileage-miles"
                type="number"
                min="0.1"
                step="0.1"
                placeholder="0.0"
                value={miles}
                onChange={(e) => setMiles(e.target.value)}
                required
                className="px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="mileage-round-trip"
              type="checkbox"
              checked={roundTrip}
              onChange={(e) => setRoundTrip(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-[#0070F2] focus:ring-[#0070F2]/30"
            />
            <label className="text-sm text-slate-700 cursor-pointer" htmlFor="mileage-round-trip">
              Round trip (doubles the miles)
            </label>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-slate-700" htmlFor="mileage-purpose">
              Business purpose
            </label>
            <input
              id="mileage-purpose"
              type="text"
              placeholder="Client meeting, site visit, etc."
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
            />
          </div>

          {/* Live preview */}
          {milesNum > 0 && (
            <div className="px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-800">
              Total:{" "}
              <span className="font-semibold">
                {totalMiles.toFixed(1)} miles
              </span>{" "}
              × ${IRS_RATE.toFixed(2)} ={" "}
              <span className="font-bold">{amountDisplay}</span>
            </div>
          )}

          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2.5 bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-medium rounded-xl text-sm transition-colors"
            >
              {submitting ? "Logging…" : "Log mileage"}
            </button>
          </div>
        </form>
      </section>

      {/* Mileage history */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-base font-semibold text-slate-800 mb-4">
          Mileage history
        </h2>

        {mileageTrips.length === 0 ? (
          <p className="text-sm text-slate-500">No trips logged yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-100">
                <tr className="text-xs uppercase text-slate-500 font-semibold">
                  <th className="pb-2 pr-4">Date</th>
                  <th className="pb-2 pr-4">Route</th>
                  <th className="pb-2 pr-4 text-right">Miles</th>
                  <th className="pb-2 pr-4 text-right">Amount</th>
                  <th className="pb-2">Purpose</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {mileageTrips.map((trip) => {
                  // Derive miles from amountCents: amountCents = miles * IRS_RATE * 100
                  const derivedMiles = trip.amountCents / (IRS_RATE * 100);
                  return (
                    <tr key={trip.id} className="hover:bg-slate-50">
                      <td className="py-2.5 pr-4 text-slate-600 whitespace-nowrap">
                        {trip.occurredOn}
                      </td>
                      <td className="py-2.5 pr-4 text-slate-800 font-medium">
                        {trip.vendor}
                      </td>
                      <td className="py-2.5 pr-4 text-slate-600 text-right whitespace-nowrap">
                        {derivedMiles.toFixed(1)}
                      </td>
                      <td className="py-2.5 pr-4 text-slate-800 font-medium text-right whitespace-nowrap">
                        {formatCents(trip.amountCents)}
                      </td>
                      <td className="py-2.5 text-slate-500 text-sm">
                        {trip.businessPurpose ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
